import React from 'react';
import { useSurveyStore, QUESTION_TYPES } from '../../surveyutilities/surveyStore';
import TextBlock from '../blocks/TextBlock';
import TextAreaBlock from '../blocks/TextAreaBlock';
import SingleChoiceBlock from '../blocks/SingleChoiceBlock';
import MultipleChoiceBlock from '../blocks/MultipleChoiceBlock';
import DropdownBlock from '../blocks/DropdownBlock';
import RatingBlock from '../blocks/RatingBlock';
import FileUploadBlock from '../blocks/FileUploadBlock';
import BlockControls from './BlockControls';
import BlockSettingsControl from './BlockSettingsControl';

const TYPE_META = {
    [QUESTION_TYPES.TEXT]: { label: 'Single-line', icon: 'bi-input-cursor-text' },
    [QUESTION_TYPES.TEXTAREA]: { label: 'Multi-line', icon: 'bi-textarea-t' },
    [QUESTION_TYPES.SINGLE_CHOICE]: { label: 'Radio', icon: 'bi-ui-radios' },
    [QUESTION_TYPES.MULTIPLE_CHOICE]: { label: 'Checkbox', icon: 'bi-ui-checks' },
    [QUESTION_TYPES.DROPDOWN]: { label: 'Dropdown', icon: 'bi-menu-button-wide' },
    [QUESTION_TYPES.RATING]: { label: 'Rating', icon: 'bi-star' },
    [QUESTION_TYPES.FILE_UPLOAD]: { label: 'Upload', icon: 'bi-upload' },
};

const MainBlock = React.memo(
    ({
        block,
        index,
        totalBlocks,
        isDragging = false,
        isDragOver = false,
        onDragStart,
        onDragOver,
        onDrop,
        onDragEnd,
    }) => {
        const selectedQuestionId = useSurveyStore((state) => state.selectedQuestionId);
        const selectQuestion = useSurveyStore((state) => state.selectQuestion);

        const isSelected = selectedQuestionId === block.internalId;
        const isFirst = index === 0;
        const isLast = index === totalBlocks - 1;
        const typeMeta = TYPE_META[block.type] || { label: block.type, icon: 'bi-question-circle' };

        const renderBlockContent = () => {
            switch (block.type) {
                case QUESTION_TYPES.TEXT:
                    return <TextBlock block={block} position={index} />;
                case QUESTION_TYPES.TEXTAREA:
                    return <TextAreaBlock block={block} position={index} />;
                case QUESTION_TYPES.SINGLE_CHOICE:
                    return <SingleChoiceBlock block={block} position={index} />;
                case QUESTION_TYPES.MULTIPLE_CHOICE:
                    return <MultipleChoiceBlock block={block} position={index} />;
                case QUESTION_TYPES.DROPDOWN:
                    return <DropdownBlock block={block} position={index} />;
                case QUESTION_TYPES.RATING:
                    return <RatingBlock block={block} position={index} />;
                case QUESTION_TYPES.FILE_UPLOAD:
                    return <FileUploadBlock block={block} position={index} />;
                default:
                    return <div className="text-muted small">Unknown block type: {block.type}</div>;
            }
        };

        return (
            <div
                className={`ecx-survey-block${isSelected ? ' is-selected' : ''}${
                    isDragging ? ' is-dragging' : ''
                }${isDragOver ? ' is-drag-over' : ''}`}
                onDragOver={(event) => onDragOver?.(event, block.internalId)}
                onDrop={(event) => onDrop?.(event, block.internalId)}
                onDragEnd={onDragEnd}
            >
                <div
                    className="ecx-survey-block__card"
                    role="button"
                    tabIndex={0}
                    onClick={() => selectQuestion(block.internalId)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            selectQuestion(block.internalId);
                        }
                    }}
                >
                    <div className="ecx-survey-block__meta">
                        <div className="ecx-survey-block__meta-left">
                            <span
                                className="ecx-survey-block__drag"
                                title="Drag to reorder"
                                draggable
                                onDragStart={(event) => {
                                    event.stopPropagation();
                                    onDragStart?.(event, block.internalId);
                                }}
                                onClick={(event) => event.stopPropagation()}
                            >
                                <i className="bi bi-grip-vertical" />
                            </span>
                            <span className="ecx-survey-block__type">
                                <i className={`bi ${typeMeta.icon}`} />
                                {typeMeta.label}
                            </span>
                        </div>
                        {block.required ? (
                            <span className="ecx-survey-block__badge">Required</span>
                        ) : null}
                    </div>

                    {renderBlockContent()}

                    {isSelected && (
                        <>
                            <BlockControls block={block} isFirst={isFirst} isLast={isLast} />
                            <BlockSettingsControl block={block} />
                        </>
                    )}
                </div>
            </div>
        );
    }
);

MainBlock.displayName = 'MainBlock';

export default MainBlock;
